/**
 * ScopedQueryProvider — conecta o TanStack Query (QueryProvider) à árvore roteada,
 * derivando o escopo de tenant/BU a partir dos claims do token autenticado.
 *
 * Antes do login (sem usuário), usa um escopo sentinela ("anonymous"): as telas
 * públicas (login/convite) não fazem queries de tenant. Após o login, o escopo
 * passa a refletir tenant_id/business_unit_id do JWT, e o QueryProvider isola o
 * cache por tenant (RNF 2.3).
 *
 * A lógica de decodificação do JWT foi extraída para `shared/auth/tokenClaims.ts`
 * para ser reutilizada em outros fluxos (ex.: CreateAccountDialog).
 */

import { useMemo, type ReactNode } from "react";
import { QueryProvider, type TenantScope } from "./providers/QueryProvider.js";
import { useActiveScope } from "./providers/ActiveScopeProvider.js";

export function ScopedQueryProvider({ children }: { children: ReactNode }) {
  // O escopo de cache segue a BU ATIVA (ActiveScopeProvider). Assim, ao trocar de
  // BU no seletor do Shell, a queryKey muda e o react-query refaz as queries
  // escopadas — em vez de servir dados antigos do cache da BU anterior.
  const { tenantId, businessUnitId } = useActiveScope();

  const scope: TenantScope = useMemo(
    () => ({
      tenantId: tenantId ?? "anonymous",
      businessUnitId: businessUnitId ?? null,
    }),
    [tenantId, businessUnitId],
  );

  return <QueryProvider scope={scope}>{children}</QueryProvider>;
}
